import { defineCopy } from '@floorfile/i18n'

/**
 * Checks — the personal queue, the supervision register, the schedule, the
 * plan wizard, the technician run screen and the settings catalogue
 * (localization plan Phase 5a, chunk 5a-5).
 *
 * ## Three weekday arrays, in one slice
 *
 * `WEEKDAY_LABELS` in `lib.ts`, `WEEKDAYS` in `PlanWizard`, and
 * `WEEKDAY_HEADERS` in `ScheduleCalendar` — three hand-written
 * `['Sun', 'Mon', …]` lists, all permanently English, none reachable from a
 * translator. All three now come from `useFormat().weekday(index, 'short')`;
 * the RRULE codes map to the index through `WEEKDAY_INDEX` in `lib.ts`. Six of
 * the original eleven remain in `apps/web`, one per unmigrated slice.
 *
 * ## `recurrence` replaces TWO sentence builders that disagreed
 *
 * `humanizeRecurrence` (`lib.ts`, for a saved plan) and `humanize`
 * (`PlanWizard`, for the live preview) both turned an RRULE into plain
 * language — the spec's "RRULE is never shown" rule — and they had drifted:
 * the wizard printed the raw `06:00` where the list printed `6:00 AM`, and the
 * wizard had a `Weekly at …` form the list could not produce. Both read these
 * messages now, so the preview and the saved plan finally say the same thing.
 *
 * ## `status` was the raw enum on every surface
 *
 * The badge printed `IN_PROGRESS`, and the filter chips printed
 * `s.replace('_', ' ')` — `IN PROGRESS`. Both are the enum, not a label. The
 * words below are what the run screen already implied.
 */
export const checksCopy = defineCopy('checks', {
  offlineAction: { start: 'Start check', complete: 'Complete check' },
  /** `CheckStatus`. Was the raw enum in the badge and `replace('_',' ')` in the chips. */
  status: {
    OPEN: 'Open',
    IN_PROGRESS: 'In progress',
    DONE: 'Done',
    FAILED: 'Failed',
    MISSED: 'Missed',
    CANCELLED: 'Cancelled',
  },
  /**
   * FF-AUDIT-20260921-210633-025: a failed check that raised a CAR carries a
   * `resolutionStatus` (`CAR_OPEN` → `RESOLVED`), which the CAR's source card
   * and the problem lineage project as the check's status. It rendered raw.
   */
  resolutionStatus: {
    CAR_OPEN: 'Failed · CAR open',
    RESOLVED: 'Resolved',
  },
  /** Overdue outranks the stored status on the badge — it is not a status value. */
  overdue: 'Overdue',

  /** `CheckType.category`. Rendered raw in two `<select>`s. */
  category: {
    QUALITY: 'Quality',
    EQUIPMENT: 'Equipment',
    SAFETY: 'Safety',
    SIGNOFF: 'Sign-off',
    SUPERVISOR: 'Supervisor',
    CUSTOM: 'Custom',
  },
  /** The same values inside "{category} checks ×" — not `.toLowerCase()`. */
  categoryInline: {
    QUALITY: 'quality',
    EQUIPMENT: 'equipment',
    SAFETY: 'safety',
    SIGNOFF: 'sign-off',
    SUPERVISOR: 'supervisor',
    CUSTOM: 'custom',
  },

  /** Plan lifecycle. The stored values are lowercase and reached the badge raw. */
  planStatus: {
    active: 'Active',
    paused: 'Paused',
    ended: 'Ended',
  },

  /**
   * Plain-language recurrence. **RRULE is never shown** (spec §7), so this is
   * the only description a planner ever reads.
   *
   * One message per shape rather than a stem plus appended clauses: French
   * puts the time before the day list in some of these, and `Every {days} at
   * {time}` fixes an order English happens to allow.
   */
  recurrence: {
    once: 'Once, at {time}',
    daily: 'Every day at {time}',
    dailyInterval: 'Every {count} days at {time}',
    weekly: 'Every week at {time}',
    weeklyInterval: 'Every {count} weeks at {time}',
    weekday: 'Every weekday at {time}',
    onDays: 'Every {days} at {time}',
    onDaysInterval: 'Every {count} weeks on {days} at {time}',
    monthly: 'Every month at {time}',
    monthlyInterval: 'Every {count} months at {time}',
    /** An RRULE shape this projection does not model — still says something. */
    fallback: 'Recurring at {time}',
  },

  page: {
    title: 'Checks',
    description: 'Routine verification — created, assigned, executed and followed up automatically.',
    tabs: 'Checks views',
    /** Tab LABELS keyed by the tab's own id — the id is the `?tab=` slug. */
    tab: {
      mine: 'My Checks',
      all: 'All Checks',
      schedule: 'Schedule',
    },
    newPlan: '+ New Plan',
    newCheck: '+ New check',
    /** The summary chips. `{label} ({count})` is one message — see `chip`. */
    chipOverdue: 'Overdue',
    chipDueToday: 'Due today',
    chipFailedThisWeek: 'Failed this week',
    chip: '{label} ({count})',
    /** The settings pointer under the tabs. One message, one slot. */
    settingsHint: 'Check types and form assignment are configured in <0>Settings → Checks</0>.',
  },

  /** The personal queue. */
  mine: {
    loading: 'Loading…',
    emptyTitle: "You're all caught up 🎉",
    emptyDescription: 'No checks assigned to you right now.',
    /** Group headings. `{title} ({count})` so the count can move. */
    group: '{title} ({count})',
    overdue: 'Overdue',
    dueToday: 'Due today',
    upcoming: 'Upcoming',
    doneToday: 'Done today',
    /** Under Late completion = LOCK: past due + grace, listed until tomorrow. */
    missedToday: 'Missed today',
    /** F8.3 — given to one of my teams, nobody has taken it yet. */
    teamChecks: 'Team checks',
    teamChecksHint: 'Given to a team you are on. Open one and it is yours; the others on the team stop seeing it.',
    /** The card's primary action, by the status it is offered for. */
    open: 'Open',
    continue: 'Continue',
    view: 'View',
    /** The register (2026-09-23: table conversion, one table per group). */
    column: { number: 'Number', title: 'Title', type: 'Type', location: 'Location', date: 'Date', due: 'Due', status: 'Status', team: 'Team' },
  },

  /** The supervision register. */
  all: {
    region: 'Checks',
    /** Drill-through chip (FF-AUDIT-032): a completion-dated window has no
     * control on this bar, so the strip names it. Dates are the URL's
     * calendar days. */
    scopeCompleted: 'Completed: {from} – {to}',
    scopeMissed: 'Missed, or not started by the deadline (due time and grace)',
    /** Owner decision 2026-09-30: a drill-through scope. */
    scopeLate: 'Finished after the deadline (late)',
    /** Lowercase: read inside "Sorted by {field}, ascending". */
    sortField: {
      dueAt: 'due date',
      checkNumber: 'number',
      title: 'title',
      status: 'status',
      score: 'score',
      completedAt: 'completed',
      createdAt: 'created',
    },
    /** The Filters panel (Fiix audit Phase 1). Field captions the columns
     * do not already name; the rest reuse `column.*`. */
    filter: {
      /** The combined status entry the presets and dashboard links use. */
      anyOpen: 'Open or in progress',
    /**
     * What a first visit opens on (FF-AUDIT-20260921-210633-029).
     *
     * NOT the same set as "Open or in progress". The first attempt at this
     * default used that pair, and it hid every FAILED check — the one
     * status that most needs somebody. A failed check is unfinished work
     * with a corrective action attached to it; a missed one is unfinished
     * work nobody did. Only Done and Cancelled are finished with.
     */
    /** FF-QA-065: was “Not finished with”, which read as a truncated sentence in the chip. */
    needsAttention: 'Needs attention',
      category: 'Category',
      dueFrom: 'Due from',
      dueTo: 'Due to',
    },
    assignee: 'Assignee',
    /** F8.3 — the Team filter. */
    team: 'Team',
    me: 'Me',
    includeArchived: 'Include archived',
    searchPlaceholder: 'Search…',
    selectAll: 'Select all',
    selectRow: 'Select {number}',
    selectedCount: '{count} selected',
    checkingEligibility: 'Checking eligibility…',
    reassign: 'Reassign ({count})',
    cancelSelected: 'Cancel ({count})',
    exportSelected: 'Export {count}',
    /** Native `confirm`. A plural record — "1 check(s)" is not a sentence. */
    confirmCancelTitle: 'Cancel the selected checks?',
    confirmCancel: {
      one: 'Cancel {count} check?',
      other: 'Cancel {count} checks?',
    },
    /** FF-QA-160: finished rows in the selection are named up front, and the buttons say what they do. */
    confirmCancelPartial: {
      one: 'Cancel {count} check? {skipped} of the selected checks are already finished and stay as they are.',
      other: 'Cancel {count} checks? {skipped} of the selected checks are already finished and stay as they are.',
    },
    confirmCancelGo: 'Cancel the checks',
    confirmCancelKeep: 'Keep them',
    bulkCancelSummary: '{cancelled} cancelled · {skipped} skipped',
    cancelFailed: 'Could not cancel every selected check.',
    previewFailed: 'Could not preview reassignment eligibility.',
    reassignFailed: 'Could not reassign every selected check.',
    eligibilityIntro:
      'Eligible people passed the assignment gate for every selected check. Expand a check to review its full decision trace.',
    /** A per-record failure line, and the joined bulk error. */
    failureLine: '{checkId}: {message}',
    overrideAssignee: 'Override assignee',
    reassignTo: 'Reassign to',
    choosePerson: 'Choose a person…',
    overrideEligibility: 'Override eligibility',
    justification: 'Required override justification',
    apply: 'Apply',
    nobodyEligible:
      'No person is currently eligible for every selected check. Adjust the selection or use a justified override if permitted.',
    traceSummary: {
      one: '{number} · {title} ({count} eligible)',
      other: '{number} · {title} ({count} eligible)',
    },
    traceEligible: 'Eligible',
    traceExcluded: 'Excluded',
    traceLine: '{name}: {reason}',
    competenciesUnverified: 'Competence data could not be fully verified.',
    clearSelection: 'Clear selection',
    emptyTitle: 'No checks match',
    emptyDescription: 'Adjust the filters, or create a new check.',
    column: {
      number: 'Number',
      title: 'Title',
      type: 'Type',
      facility: 'Facility',
      location: 'Location',
      assignee: 'Assignee',
      due: 'Due',
      status: 'Status',
      score: 'Score',
      /** FF-AUDIT-069: the row actions have their own (visually silent) column. */
      actions: 'Actions',
    },
    archived: 'Archived',
    /** FF-AUDIT-081: an open check whose equipment has since been retired. */
    equipmentRetired: 'Equipment retired',
    /** Owner decision 2026-09-30: finished after its deadline (due time + grace). */
    late: 'Late',
    reopen: 'Reopen',
    /** FF-AUDIT-20260914-210459-070: archiving asks first and can be undone. */
    archiveDialog: {
      title: 'Archive {number}?',
      liveImpact: 'This check is still open. Archiving hides it from every list, including the person it is assigned to — the work is not cancelled, only out of sight.',
      closedImpact: 'Archiving hides this closed check from the lists.',
      restorable: 'Archived checks stay on record and can be restored from “Include archived”.',
      keep: 'Keep it',
      confirm: 'Archive',
      archiving: 'Archiving…',
    },
    archivedUndo: 'Archived {number}.',
    undo: 'Undo',
    dismiss: 'Dismiss',
    archive: 'Archive',
    restore: 'Restore',
    /** The score column. The percent SIGN's position is the locale's. */
    score: '{score}%',
    /** Row overflow menu (Fiix audit Phase 0): every row action lives here. */
    rowMenu: 'More actions for {number}',
    openRecord: 'Open',
    /** The scope chip when the register was opened from a plan's "View its checks". */
    scopePlan: 'Plan: {plan}',
  },

  /** The Schedule tab. */
  schedule: {
    viewSwitcher: 'Schedule view',
    viewList: 'List',
    viewCalendar: 'Calendar',
    emptyTitle: 'No plans yet',
    emptyDescription: 'Create a plan to generate checks automatically.',
    next: 'Next: {dates}',
    /** Owner, 2026-10-01: what keeps a plan's checks coming, said where the plans are. */
    autoHint: 'Each active plan keeps the next 14 days of its checks on people’s lists, and tops them up automatically every day.',
    edit: 'Edit',
    /** All of the plan's checks in the All Checks register. */
    viewChecks: 'View its checks',
    pause: 'Pause',
    resume: 'Resume',
    end: 'End',
    /** The register (2026-09-23: table conversion). */
    region: 'Schedule',
    column: {
      number: 'Number',
      title: 'Plan',
      type: 'Type',
      location: 'Location',
      assignee: 'Assigned to',
      recurrence: 'Recurrence',
      next: 'Next occurrences',
      status: 'Status',
    },
    /** Who a plan's checks go to, by assignment mode. */
    assigneeTeam: '{team} (team)',
    assigneeResolved: 'Decided for each check',
    assigneeRotation: 'Rotation: {names}',
    searchPlaceholder: 'Search plans…',
    filter: { status: 'Status', type: 'Type', includeArchived: 'Include removed plans' },
    noMatchTitle: 'No plans match',
    noMatchDescription: 'Adjust the filters, or create a new plan.',
    rowMenu: 'More actions for {title}',
    /** Native `confirm` before an irreversible end. */
    confirmEnd: 'End "{title}"? This is permanent — history is kept.',
    /** Titles for the shared dialogs that replaced the bare `confirm()`. */
    confirmEndTitle: 'End this plan?',
    /** Owner, 2026-10-01: removing a plan (soft delete) and undoing it. */
    remove: 'Remove',
    restore: 'Restore',
    removed: 'Removed',
    confirmRemoveTitle: 'Remove this plan?',
    confirmRemove:
      '{number} “{title}” is taken off the schedule and stops creating checks. Its upcoming checks that haven’t been started are withdrawn; started, completed and overdue checks are kept. You can restore it later.',
    confirmRemoveGo: 'Remove plan',
    removedUndo: '{number} removed.',
    undo: 'Undo',
    dismiss: 'Dismiss',
  },

  /** The month calendar under the Schedule tab. */
  calendar: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    region: 'Schedule calendar',
    /** Owner, 2026-10-01: the same four views as the work-order calendar. */
    viewSwitcher: 'Calendar view',
    viewMonth: 'Month',
    viewWeek: 'Week',
    viewAgenda: 'Agenda',
    viewWorkload: 'Workload',
    weekHeading: '{from} – {to}',
    agendaHeading: 'Next 14 days · {from} – {to}',
    hint: 'Every scheduled check from the active plans, in the company’s time zone. Select one to open its plan.',
    workloadHint: 'Scheduled checks per person per day for the week shown.',
    workloadRegion: 'Check workload',
    person: 'Assigned to',
    weekTotal: 'Week',
    laneCell: { one: '{count} check', other: '{count} checks' },
    more: '+{count} more',
    nothingDue: 'Nothing due',
    nothingDueFortnight: 'No checks are scheduled in the next two weeks.',
    openPlan: 'Open the plan {title}',
  },

  /** "New check — right now": the ad-hoc check a supervisor raises on the floor. */
  newCheck: {
    title: 'New check — right now',
    what: 'What are you checking?',
    type: 'Type',
    /** FF-AUDIT-20260921-210633-028: a safety check OF a machine can name it. */
    equipmentOptional: 'Equipment (optional)',
    noEquipment: 'No specific machine',
    equipmentOptionalHelp: 'Naming the machine puts this check in its history and lets the corrective playbook act when it fails.',
    equipment: 'Equipment',
    location: 'Location',
    choose: 'Choose…',
    form: 'Form (optional)',
    /** Three mutually exclusive states for the "no explicit form" option. */
    assignedForm: 'Assigned form: {name}',
    assignedFormUnavailable: 'Assigned form (not available)',
    typeDefaultForm: "Type's default form",
    noForm: 'No form',
    unpublishedWarning:
      'The assigned form is not published — this check will open without it unless the type has a default form.',
    lot: 'Lot being checked (optional)',
    assignedNote: 'Assigned to you · due end of today.',
    /** Owner finding 2026-09-19: managers choose who does it. */
    whoDoesIt: 'Who does it',
    assignMe: 'Me',
    assignOther: 'Someone else',
    /** F8.3 — give it to a team; the first member to open it takes it. */
    assignTeam: 'A team',
    team: 'Team',
    chooseTeam: 'Choose a team',
    teamHint: 'Everyone on the team is told. The first of them to open it takes it.',
    teamsFailed: 'Teams could not be loaded.',
    submitForTeam: 'Give to the team',
    chooseEquipment: 'Choose the equipment this check is for.',
    assignee: 'Assign to',
    chooseWhereFirst: 'Choose the location or equipment first — who can take this depends on where it is.',
    dueNote: 'Due end of today.',
    overrideHeading: 'This person is not normally eligible',
    overrideExplain: '{name} was excluded — {reason}. You may assign them anyway; the reason below is recorded against the check.',
    overrideReason: 'Why is this the right person?',
    overrideReasonPlaceholder: 'e.g. Covering while the qualified operator is on leave; supervised by the line lead.',
    submitForOther: 'Create & assign',
    cancel: 'Cancel',
    creating: 'Creating…',
    submit: 'Create & start',
    failed: 'Could not create check.',
  },

  /** The three-step plan wizard. */
  wizard: {
    title: 'New check plan',
    /** Owner, 2026-10-01: the same three steps edit a plan. */
    editTitle: 'Edit {number}',
    step: 'Step {step} of 3',
    planTitle: 'Title',
    description: 'Description (optional)',
    titlePlaceholder: 'Line 3 sanitation',
    checkType: 'Check type',
    choose: 'Choose…',
    equipment: 'Equipment',
    location: 'Location',
    /** The location follows the asset for an equipment plan — said, not implied. */
    locationFollowsEquipment: 'Location (follows the equipment)',
    form: 'Form (optional)',
    assignedForm: 'Assigned form: {name}',
    assignedFormBare: 'Assigned form',
    noForm: 'No form',
    /** F4 — a ready-made checklist; it arrives as a draft form to publish first. */
    fromLibrary: 'Find one in the library',
    once: 'Just once',
    recurring: 'Repeating',
    frequency: 'Frequency',
    freq: {
      DAILY: 'Daily',
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
    },
    time: 'Time',
    startDate: 'Start date',
    endDate: 'End date (optional)',
    endBeforeStart: 'The end date is before the start date.',
    /** A rule from an import or the API that these controls cannot show. */
    customRule: 'This plan repeats on a custom rule these settings can’t show. Leave them alone to keep it; change them to replace it.',
    /** The `…` is a truncation mark, not a word — it stays outside the message. */
    next: 'Next: {dates}',
    assignHeading: 'How should each check be assigned?',
    fixedMode: 'Choose a person',
    fixedModeHint: 'The same person receives every check.',
    resolvedMode: 'Let FloorFile decide',
    resolvedModeHint: 'Resolve the best person for each occurrence.',
    /** F8.3 — every check of the plan to a team. */
    teamMode: 'Give it to a team',
    teamModeShort: 'The first team member to open each check takes it.',
    teamModeHint: 'Everyone on the team is told when a check is due. The first of them to open it takes it.',
    summaryTeam: 'Each check goes to the team; the first member to open it takes it',
    requirementsHeading: 'Assignment requirements',
    requirementsHint:
      'At every occurrence, FloorFile checks qualifications, location access, shift availability, and workload before assigning. Only workers with login accounts can be assigned — directory-only workers are not eligible.',
    requiredJobTitle: 'Required job title',
    anyJobTitle: 'Any job title',
    requiredRole: 'Required role',
    anyRole: 'Any role',
    roleSupervisor: 'Supervisor',
    roleAdmin: 'Admin',
    oneRequired: 'Set a job title, a role, or both — at least one is required.',
    requiredCertifications: 'Required certifications (optional)',
    certificationsPlaceholder: 'LOTO, Forklift',
    certificationsHint: 'Separate certification names with commas.',
    strategyLabel: 'Choose among qualified people by',
    strategy: {
      LEAST_LOADED: 'Lowest current workload',
      ROUND_ROBIN: 'Fairest rotation',
      FIRST_AVAILABLE: 'First available on shift',
    },
    escalationLabel: 'If nobody qualifies on shift',
    escalation: {
      ON_SHIFT_SUPERVISOR: 'Escalate to the on-shift supervisor',
      RELAX_TO_QUALIFIED_OFFSHIFT: 'Call in a qualified off-shift person',
    },
    graceWindow: 'Grace window',
    grace: {
      2: '2 hours',
      4: '4 hours',
      8: '8 hours',
      24: 'End of day',
    },
    /** A plan saved with another grace (the API takes 0–72). */
    graceOther: { one: '{count} hour', other: '{count} hours' },
    requireSignature: 'Require signature on completion',
    untitled: 'Untitled',
    summaryFixed: 'Assigned to one selected person',
    summaryResolved: 'FloorFile resolves each occurrence',
    summaryResolvedForTitle: 'FloorFile resolves each occurrence for {jobTitle}',

    /**
     * Audit 08-12 B1.1 — what "Choose a person" actually commits to, stated
     * before the plan is created rather than discovered on the first
     * occurrence. Four separate facts, so a translator can order the sentence
     * naturally and a reader can check each one.
     */
    chosenPerson: 'Assigned to {name}',
    chosenFacility: 'At {facility}',
    chosenEffective: 'First check due {when}',
    /** FF-QA-062: the chosen time already passed today. */
    chosenEffectivePast: '{when} has passed — the first check is the next occurrence after now.',
    chosenEligible: 'Passes the eligibility checks',
    chosenWarned: 'Eligible, but may not be on shift',
    chosenOverridden: 'Not normally eligible — {reason}',

    /**
     * Audit 08-12 B1.2 — naming somebody the gate rejected. Deliberately worded
     * as a decision being recorded rather than a warning being dismissed.
     */
    overrideHeading: 'This person is not normally eligible',
    /**
     * `{reason}` is a STANDALONE sentence from the assignment catalogue, not a
     * clause — so it is set off with a dash rather than embedded after
     * "because", which read as "excluded because This location is…". The same
     * string is also rendered on its own as a list item and a chip; wording it
     * to fit only inside this sentence would break the other two.
     */
    overrideExplain:
      '{name} was excluded — {reason}. You may assign them anyway; the reason below is recorded against the plan.',
    overrideReason: 'Why is this the right person?',
    overrideReasonPlaceholder: 'e.g. Covering while the qualified operator is on leave; supervised by the line lead.',
    overrideMissing: 'Give a reason before creating the plan.',
    overrideMissingEdit: 'Give a reason before saving.',
    /** Edit mode: the form pin, which can be cleared. */
    formFollowRules: 'Follow form-assignment rules (no pinned form)',
    formLoadFailed: 'The list of published forms could not be loaded — the current choice is kept until you pick another.',
    unpublishedTag: '{name} (not published — checks get no form)',
    /** What saving an edit does, said before it is done. */
    reconcileWarning:
      'Saving rebuilds this plan’s checks that are scheduled but not started. Started and completed checks are never changed.',
    cancel: 'Cancel',
    back: 'Back',
    nextStep: 'Next',
    creating: 'Creating…',
    submit: 'Create plan',
    failed: 'Could not create plan.',
    saving: 'Saving…',
    save: 'Save changes',
    saveFailed: 'Could not save the plan.',
  },

  reopen: {
    title: 'Reopen this check?',
    /**
     * One message with two emphasis slots. It was four JSX fragments around
     * two `<strong>`s, which fixes the clause order in English.
     */
    body: 'This returns the check to <0>Open</0> so it can be completed. It was marked <1>{status}</1>, and reopening is recorded in the audit trail.',
    statusCancelled: 'cancelled',
    statusMissed: 'missed',
    remindersNote: 'Overdue reminders re-arm, so this check can notify again if it passes its due time.',
    cancel: 'Cancel',
    reopening: 'Reopening…',
    submit: 'Reopen check',
    failed: 'Could not reopen this check.',
  },

  /** The technician run screen. */
  run: {
    loading: 'Loading…',
    /** FF-AUDIT-094: the API's 404 (also what a foreign company's id gets).
     * Before this the page said `Loading…` forever. */
    notFound: 'This check does not exist, or you no longer have access to it.',
    /** FF-AUDIT-20260914-210459-096: a dead zone with nothing cached — a different fact from not found. */
    unreachableTitle: 'Can’t reach the server',
    unreachableBody: 'This check is not stored on this device yet. Reconnect and try again, or download for offline before the next dead zone.',
    retry: 'Try again',
    back: 'Back to checks',
    /** Header line: number · location · due time. */
    /** FF-AUDIT-081: the asset was retired after this check was scheduled. */
    equipmentRetired: 'Equipment retired: {name}',
    /** FF-AUDIT-045: the asset the check is about, linked. */
    equipmentLink: 'Equipment: {name}',
    /** Fiix audit Phase 2: the record rail and the header's overflow. */
    moreActions: 'More actions for this check',
    facts: {
      region: 'Check details',
      location: 'Location',
      due: 'Due',
      assignee: 'Assignee',
      equipment: 'Equipment',
    },
    /** FF-AUDIT-044: the check's own description and notes, on the run page. */
    instructionsRegion: 'Instructions and notes',
    instructionsHeading: 'Instructions',
    notesHeading: 'Notes',
    edit: 'Edit details',
    /** FF-QA-060: the record's own reassign / cancel / archive. */
    reassign: 'Reassign…',
    cancelCheck: 'Cancel check…',
    cancelConfirmTitle: 'Cancel this check?',
    cancelConfirmBody: '{number} is cancelled and leaves everyone’s list. It can be reopened by an administrator.',
    cancelConfirmGo: 'Cancel the check',
    cancelConfirmKeep: 'Keep it',
    cancelled: '{number} cancelled.',
    archive: 'Archive',
    archived: '{number} archived.',
    reassignDialog: {
      title: 'Reassign {number}',
      assignee: 'Give it to',
      cancel: 'Cancel',
      submit: 'Reassign',
      done: '{number} reassigned.',
    },
    assignedTo: 'Assigned to {name}.',
    someoneElse: 'someone else',
    /** F8.3 — team checks. */
    withTeam: 'With the {team} team — nobody has taken it yet.',
    personOfTeam: '{name} ({team})',
    teamNotTaken: '{team} · not taken yet',
    teamCheckBanner: 'Given to the {team} team. The first of you to open it takes it.',
    claimedBy: '{name} has already taken this check.',
    claimedBySomeone: 'Someone else on the team has already taken this check.',
    taking: 'Taking…',
    takeOwnership: 'Take ownership',
    ownershipFailed: 'Could not take ownership.',
    completeFailed: 'Could not complete.',
    checkFailed: 'Check failed',
    checkComplete: 'Check complete',
    /** The tally: two real plurals (FF-QA-119 — French wrote « 1 échoués »),
     * then `{na}` and `{score}` as whole clauses so they can move. */
    tallyPassed: { one: '{count} passed', other: '{count} passed' },
    tallyFailed: { one: '{count} failed', other: '{count} failed' },
    tallyJoin: ' · ',
    tallyNa: ' · {count} N/A',
    tallyScore: ' · {score}%',
    reviewHeading: 'Completed check review',
    loadingAnswers: 'Loading recorded answers…',
    answersFailed: 'Recorded answers could not be loaded.',
    carResolved: 'Resolved — view CAR',
    carRaised: 'CAR raised — view corrective action',
    /** FF-QA-034: what the failed-check playbooks did, on the check itself. */
    automation: {
      heading: 'What happened automatically',
      pending: 'The automation is running — what it did will appear here within a minute or two.',
      none: 'No automation ran for this check.',
      open: 'Open {number}',
      /** An outcome the catalogue has no sentence for: the step's code, then the server's note. */
      rawStep: '{action}: {note}',
    },
    raisingCar: 'Raising CAR…',
    raiseCar: 'Raise CAR',
    /** FF-QA-043 (owner, 2026-09-27): one tap to the settings default, or a preview first. */
    raiseCarNow: 'Raise CAR now',
    raiseCarPreview: 'Raise CAR…',
    raiseCarDialog: {
      title: 'Raise a corrective action',
      description: 'Check the title and choose who owns it. The failed items and the check are written into the CAR automatically.',
      carTitle: 'Title',
      defaultTitle: 'Failed check: {title}',
      descriptionNote: 'The description lists the failed items, the location and the completion date.',
      assignMode: 'Who owns it',
      modeDefaultNamed: 'Default: {name}',
      modeDefaultEngine: 'Default: on-shift supervisor',
      modeMe: 'Me',
      modeOther: 'Someone else',
      assignTo: 'Assign to',
      cancel: 'Cancel',
      submit: 'Raise CAR',
    },
    carFailed: 'Could not raise the CAR.',
    recordedForSupervisors: 'This failure is recorded and visible to supervisors.',
    wasCancelled: 'This check was cancelled',
    wasMissed: 'This check was missed',
    cancelledExplain: 'Cancelled checks cannot be completed. It has to be reopened first.',
    missedExplain:
      'It passed its due time without being completed, so it cannot be filled in as it stands. It has to be reopened first.',
    reopen: 'Reopen check',
    /** Names the real remedy — "reopen it first" reads as an instruction to
     * someone who cannot do it. */
    askAdmin: 'Ask an administrator to reopen it if this check still needs to be done.',
    /** Owner decision 2026-09-30: under Lock, past its final deadline a check is closed for good. */
    closedExplain: 'It is past its deadline and is closed. It can no longer be completed or reopened.',
    loadingForm: 'Loading form…',
    formFailed: 'Could not load this check’s form — check your connection and try again.',
    noFormLead: 'No form is attached to this check — record the outcome directly.',
    configureRouting: 'Configure form assignment',
    outcome: 'Check outcome',
    pass: 'Pass',
    fail: 'Fail',
    whatFailed: 'What failed?',
    completing: 'Completing…',
    complete: 'Complete check',

    /**
     * Audit 08-12 B4 — a check that was MEANT to have a form and did not get
     * one. Distinct from `noFormLead` above, which is the ordinary case of a
     * check nobody assigned a form to: this one is a configuration fault, and
     * saying "record the outcome directly" here sent people down a path that
     * then refused their signature.
     */
    /** FF-AUDIT-065: the form version the check was created under has since
     * been retired by a newer publish; the answers still go to THAT version. */
    retiredVersionNotice: 'This check uses version {version} of its form, which has since been replaced by a newer version. Your answers are recorded against version {version}.',
    blocked: {
      heading: 'This check cannot be filled in yet',
      /**
       * The NON-blocking heading. `not_attached` means the configuration is
       * now right and only this check predates it — publishing the form is
       * what clears the block, and this is the state that then remains. It
       * offers the form; it does not take the manual outcome away.
       */
      availableHeading: 'A form is ready for this check',
      /** `{form}` is the form’s name; the reason clause is a separate slot so
       * it can be translated as a whole sentence in each language. */
      lead: 'It should load “{form}”, but {reason}',
      leadUnnamed: 'The form it should load {reason}',
      reason: {
        not_found: 'that form no longer exists.',
        no_published_version: 'that form has no published version yet.',
        not_attached: 'this check was created without a form attached.',
        /** FF-AUDIT-059: the check came from a plan whose PINNED form could not
         * be used, while a form-assignment rule now resolves one. */
        plan_form_unresolved: 'this check was generated by the plan “{plan}”, whose pinned form can no longer be used.',
      },
      /** Link beside the attach button when the fix belongs on the plan. */
      fixPlan: 'Fix the plan’s form',
      source: {
        explicit: 'The form was picked directly on this check.',
        routing_rule: 'It comes from a form-assignment rule for this check’s type and location.',
        type_default: 'It is the default form for this check’s type.',
        /** FF-AUDIT-059: nothing else routes a form; the plan's pin is the only candidate. */
        plan: 'It was pinned on the plan that generates this check, and no form-assignment rule or type default applies.',
      },
      /** For someone who cannot fix it themselves. */
      askAdmin: 'Ask an administrator to publish the form, or to change which form this kind of check uses.',
      openForm: 'Open the form',
      openRouting: 'Change form assignment',
      /** Shown once an administrator has published it: pull it onto this
       * record, which publishing alone does not do. */
      attach: 'Attach the form now',
      attaching: 'Attaching…',
      attachFailed: 'Could not attach the form.',
    },

    /** Check-level signature capture (audit 08-12 B4.4). */
    signature: {
      heading: 'Signature',
      lead: 'This check has to be signed before it can be completed.',
      missing: 'Sign above before completing this check.',
    },
  },

  /** Settings → Checks. */
  settings: {
    noAccess: 'You do not have access to check settings.',
    title: 'Checks',
    description: 'Check types, and which form each check loads.',
    tabs: 'Check settings views',
    tabTypes: 'Check types',
    tabRouting: 'Form assignment',
    /** Owner decision 2026-09-18: what happens to a check nobody completed in time. */
    tabLate: 'Late completion',
    lateHeading: 'Checks completed after their due time',
    lateHint:
      'Every check has a grace window after its due time; the end of it is the check’s deadline. Under “Lock” a check nobody has started by the deadline is recorded as missed, disappears from the assignee’s list the next day and is closed — it can no longer be completed or reopened. What happens to a check started before the deadline is set below. Under “Allow” a check stays open and can still be completed late. Either way, a check finished after its deadline is reported as late.',
    latePolicy: 'After the grace window',
    /** F35: shown under the select. */
    latePolicyHelp: 'Decides whether a check can still be completed once its grace window closes.',
    latePolicyLock: 'Lock — a check not started in time is missed and cannot be completed',
    latePolicyAllow: 'Allow — the check stays open and can be completed late',
    graceDefault: 'Grace window for new checks and plans (hours)',
    graceDefaultHint: '0 to 72. A plan can set its own; this is the default when none is given.',
    /** Owner decision 2026-09-30: what happens to a check STARTED before its deadline (Lock only). */
    startedPolicy: 'Checks started before the deadline',
    startedPolicyHelp: 'Whether a worker who started a check in time may still finish it after the deadline.',
    startedPolicyExtend: 'Extend the time limit on started checks',
    startedPolicyMiss: 'Missed at the deadline, like any unfinished check',
    startedExtensionHours: 'Extra time for started checks (hours)',
    startedExtensionHint: '1 to 72. How long after the deadline a started check can still be finished. After that it is missed and closed.',
    lateSave: 'Save late-completion policy',
    lateSaved: 'Late-completion policy saved.',
    lateSaveFailed: 'The policy could not be saved.',
    typesHeading: 'Check types',
    typesHint:
      'Every check belongs to a type. Retiring a type hides it from pickers — existing checks and plans are untouched. The permanent code drives the number prefix; the default form loads on every check of the type unless a form-assignment rule or an explicit pick overrides it.',
    loading: 'Loading…',
    name: 'Name',
    code: 'Code',
    codePermanent: 'Code (permanent)',
    category: 'Category',
    colour: 'Colour',
    defaultForm: 'Default form',
    noForm: 'No form',
    unpublishedDefault: 'The configured default form is not published.',
    save: 'Save',
    retire: 'Retire',
    restore: 'Restore',
    retired: 'Retired',
    saveFailed: 'Could not save.',
    updateFailed: 'Could not update.',
    namePlaceholder: 'Line Clearance',
    adding: 'Adding…',
    addType: 'Add type',
    createFailed: 'Could not create the type.',
    /**
     * The swatch palette's accessible names. These ARE colour words a person
     * reads aloud, not theme tokens — the hex values stay in the component.
     */
    colourName: {
      Slate: 'Slate',
      Red: 'Red',
      Orange: 'Orange',
      Amber: 'Amber',
      Green: 'Green',
      Teal: 'Teal',
      Blue: 'Blue',
      Violet: 'Violet',
      Pink: 'Pink',
      Brown: 'Brown',
    },
  },

  /** Settings → Checks → Form assignment. */
  routing: {
    heading: 'Form assignment',
    intro:
      'When a check of a type is created for a machine or at a location, the mapped form loads automatically. A form picked directly on a check or plan always wins; without a rule, the check type’s default form applies.',
    checkType: 'Check type',
    choose: 'Choose…',
    scope: 'Scope',
    scopeEquipment: 'Equipment',
    scopeLocation: 'Location',
    form: 'Form',
    addRule: 'Add rule',
    duplicate: 'A rule for that type and target already exists — remove it first.',
    loading: 'Loading…',
    /** One message with a slot: the sentence is "no rules yet, go set defaults". */
    empty: 'No rules yet — checks use their type’s default form. <0>Set type defaults</0>',
    /** The rule row. ` at ` and ` → ` were bare JSX fragments between names. */
    rule: '{type} at {target} → {form}',
    removeRule: 'Remove rule',
    editRule: 'Edit this rule',
    updateRule: 'Update rule',
    cancelEdit: 'Cancel',
    pickAll: 'Pick a check type, a target, and a form.',
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    saving: 'Saving…',
    save: 'Save rules',
    saveFailed: 'Could not save the rules.',
  },

  /**
   * Applying a form-assignment change to checks ALREADY on the schedule
   * (audit 08-12 B4). A rule change is prospective by design — a check pins
   * the form version it was created with, so a completed record can never be
   * re-scored against a form nobody filled in. The finding was that there was
   * then no way at all to repair tomorrow's work.
   */
  retrofit: {
    action: 'Apply to scheduled checks',
    title: 'Apply this form to checks already scheduled',
    /** The rule has to exist on the server before its effect can be computed. */
    saveFirst: 'Save the rules first — then this can be applied to scheduled checks.',
    loading: 'Working out which checks this affects…',
    failed: 'Could not work out which checks this affects.',
    examined: {
      one: '{count} check that is still open was examined.',
      other: '{count} checks that are still open were examined.',
    },
    historyNote:
      'Completed, failed, missed and cancelled checks are the record of what happened, and are never changed.',
    truncated: 'Only the first {count} were examined. Run this again afterwards to continue.',
    willChangeHeading: 'Will be given “{form}”',
    leftAloneHeading: 'Left as they are',
    none: 'Nothing on the schedule needs this change.',
    /** One row: check number, title, and when it is due. */
    item: '{number} · {title} · due {due}',
    reason: {
      ALREADY_ON_TARGET_FORM: 'Already uses this form',
      PLAN_PINS_ITS_OWN_FORM: 'Its schedule names a form directly',
      MORE_SPECIFIC_RULE_WINS: 'A rule for the machine applies instead',
      ARCHIVED: 'Archived',
      ALREADY_STARTED: 'Someone has already started it',
      CHANGED_WHILE_APPLYING: 'It changed while this was running',
    },
    close: 'Close',
    cancel: 'Cancel',
    confirm: { one: 'Change {count} check', other: 'Change {count} checks' },
    applying: 'Changing…',
    applied: { one: '{count} check was given this form.', other: '{count} checks were given this form.' },
    applyFailed: 'Could not apply the change.',
  },
})

export type ChecksCopy = typeof checksCopy.en
